import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router";
import { Navigate } from "react-router";
import { ROUTES } from "./constants";
import { PrivatePage } from "@/pages/PrivatePage";
import Auth from "../pages/Auth/components/Auth";
import SignIn from "../pages/Auth/components/SignIn";
import SignUp from "../pages/Auth/components/SignUp";
import SignUpPassword from "../pages/Auth/components/SignUpPassword";
import ForgotPassword from "../pages/Auth/components/ForgotPassword";
import ResetPassword from "../pages/Auth/components/ResetPassword";
import SignOut from "../pages/Auth/components/SignOut";
import { PageSuspense } from "@/components/PageSuspense";
import Register from "../pages/Register";
import TenantSelection from "../pages/TenantSelection";
import Settings from "../pages/Settings";
import { ProfileSettings } from "../pages/Settings/ProfileSettings";
import { PasswordSettings } from "../pages/Settings/PasswordSettings";
import PermissionGuard from "../components/PermissionGuard";
import { Permissions } from "@/components/PermissionGuard/constant";

const ProjectDetails = lazy(() => import("../pages/ProjectDetails"));
const ProjectOverview = lazy(
  () => import("../pages/ProjectDetails/pages/Overview"),
);
const ProjectMembers = lazy(
  () => import("../pages/ProjectDetails/pages/Members"),
);
const ProjectFiles = lazy(() => import("../pages/ProjectDetails/pages/Files"));
const ProjectForms = lazy(() => import("../pages/ProjectDetails/pages/Forms"));
const FormDetails = lazy(() => import("../pages/FormDetails"));
const ProjectPayments = lazy(
  () => import("../pages/ProjectDetails/pages/Payments"),
);
const ProjectScheduling = lazy(
  () => import("../pages/ProjectDetails/pages/Scheduling"),
);
const ProjectActivityLog = lazy(
  () => import("../pages/ProjectDetails/pages/ActivityLog"),
);
const ProjectDiscussion = lazy(
  () => import("../pages/ProjectDetails/pages/Discussion"),
);

const Tenants = lazy(() => import("../pages/Tenants"));
const TenantDetails = lazy(() => import("../pages/TenantDetails"));

const UserDetails = lazy(() => import("../pages/UserDetails"));
const ContractingCompanyDetails = lazy(
  () => import("@/pages/Companies/ContractingCompanyDetails"),
);
const LenderCompanyDetails = lazy(
  () => import("@/pages/Companies/LenderCompanyDetails"),
);

const Properties = lazy(() => import("../pages/Properties"));

const UsersAdmins = lazy(() => import("../pages/Users/Admins"));
const UsersContractors = lazy(() => import("../pages/Users/Contractors"));
const UsersLoanOfficers = lazy(() => import("../pages/Users/LoanOfficers"));
const UsersLoanProcessors = lazy(() => import("../pages/Users/LoanProcessors"));
const UsersProjectManagers = lazy(
  () => import("../pages/Users/ProjectManagers"),
);
const UsersInspectors = lazy(() => import("../pages/Users/Inspectors"));
const UsersClients = lazy(() => import("../pages/Users/Clients"));
const Lenders = lazy(() => import("../pages/Companies/Lenders"));
const ContractingCompanies = lazy(
  () => import("../pages/Companies/Contractors"),
);
const Projects = lazy(() => import("../pages/Projects"));
const Onboarding = lazy(() => import("../pages/Onboarding"));
const Legal = lazy(() => import("../pages/Legal"));
const CookieNotice = lazy(() => import("@/pages/Legal/pages/CookieNotice"));
const PrivacyPolicy = lazy(() => import("@/pages/Legal/pages/PrivacyPolicy"));

const authRoutes: RouteObject[] = [
  {
    path: `/${ROUTES.AUTH}`,
    children: [
      {
        path: "*",
        element: <Auth />,
        children: [
          {
            path: "",
            element: <Navigate to={ROUTES.SIGN_IN} />,
          },
          {
            path: ROUTES.SIGN_IN,
            element: <SignIn />,
          },
          {
            path: ROUTES.SIGN_UP,
            element: <SignUp />,
          },
          {
            path: ROUTES.PASSWORD,
            element: <SignUpPassword />,
          },
          {
            path: ROUTES.FORGOT_PASSWORD,
            element: <ForgotPassword />,
          },
          {
            path: ROUTES.RESET_PASSWORD,
            element: <ResetPassword />,
          },
        ],
      },
    ],
  },
  {
    path: `/${ROUTES.REGISTER}`,
    element: <Register />,
  },
  {
    path: `/${ROUTES.SIGN_OUT}`,
    element: <SignOut />,
  },
  {
    path: `/${ROUTES.SELECT_TENANT}`,
    element: <TenantSelection />,
  },
];

const onboardingRoutes: RouteObject[] = [
  {
    path: ROUTES.ONBOARDING,
    element: (
      <Suspense fallback={<PageSuspense filters={false} header={false} />}>
        <Onboarding />
      </Suspense>
    ),
  },
];

const superAdminRoutes: RouteObject[] = [
  {
    path: ROUTES.TENANTS,
    element: (
      <Suspense fallback={<PageSuspense />}>
        <Tenants />
      </Suspense>
    ),
  },
  {
    path: `${ROUTES.TENANTS}/:tenantId`,
    element: (
      <Suspense fallback={<PageSuspense />}>
        <TenantDetails />
      </Suspense>
    ),
  },
];

const projectRoutes: RouteObject[] = [
  {
    path: ROUTES.PROJECTS,
    element: (
      <PermissionGuard permissions={[Permissions.Projects.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <Projects />
        </Suspense>
      </PermissionGuard>
    ),
  },
  {
    path: `${ROUTES.PROJECTS}/:projectId`,
    element: (
      <PermissionGuard permissions={[Permissions.Projects.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <ProjectDetails />
        </Suspense>
      </PermissionGuard>
    ),
    children: [
      {
        path: ROUTES.PROJECT_OVERVIEW,
        element: (
          <Suspense fallback={null}>
            <ProjectOverview />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PROJECT_MEMBERS,
        element: (
          <Suspense fallback={null}>
            <ProjectMembers />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PROJECT_FILES,
        element: (
          <Suspense fallback={null}>
            <ProjectFiles />
          </Suspense>
        ),
      },
      {
        path: `${ROUTES.FORMS}/:formId`,
        element: (
          <Suspense fallback={null}>
            <FormDetails />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PROJECT_FORMS,
        element: (
          <Suspense fallback={null}>
            <ProjectForms />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PROJECT_PAYMENTS,
        element: (
          <Suspense fallback={null}>
            <ProjectPayments />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PROJECT_SCHEDULING,
        element: (
          <Suspense fallback={null}>
            <ProjectScheduling />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PROJECT_ACTIVITY_LOGS,
        element: (
          <Suspense fallback={null}>
            <ProjectActivityLog />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PROJECT_DISCUSSIONS,
        element: (
          <Suspense fallback={null}>
            <ProjectDiscussion />
          </Suspense>
        ),
      },
    ],
  },
];

const partnersRoutes: RouteObject[] = [
  {
    path: ROUTES.PARTNERS_LENDERS,
    element: (
      <PermissionGuard permissions={[Permissions.Companies.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <Lenders />
        </Suspense>
      </PermissionGuard>
    ),
  },
  {
    path: ROUTES.PARTNERS_CONTRACTING_COMPANIES,
    element: (
      <PermissionGuard permissions={[Permissions.Companies.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <ContractingCompanies />
        </Suspense>
      </PermissionGuard>
    ),
  },
  {
    path: `${ROUTES.PARTNERS_LENDERS}/:companyId`,
    element: (
      <Suspense fallback={<PageSuspense filters={false} />}>
        <LenderCompanyDetails />
      </Suspense>
    ),
  },
  {
    path: `${ROUTES.PARTNERS_CONTRACTING_COMPANIES}/:companyId`,
    element: (
      <Suspense fallback={<PageSuspense filters={false} />}>
        <ContractingCompanyDetails />
      </Suspense>
    ),
  },
];

const usersRoutes: RouteObject[] = [
  {
    path: ROUTES.USERS_ADMINS,
    element: (
      <PermissionGuard permissions={[Permissions.Clients.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <UsersAdmins />
        </Suspense>
      </PermissionGuard>
    ),
  },
  {
    path: ROUTES.USERS_LOAN_OFFICERS,
    element: (
      <PermissionGuard permissions={[Permissions.Clients.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <UsersLoanOfficers />
        </Suspense>
      </PermissionGuard>
    ),
  },
  {
    path: ROUTES.USERS_LOAN_PROCESSORS,
    element: (
      <PermissionGuard permissions={[Permissions.Clients.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <UsersLoanProcessors />
        </Suspense>
      </PermissionGuard>
    ),
  },
  {
    path: ROUTES.USERS_PROJECT_MANAGERS,
    element: (
      <PermissionGuard permissions={[Permissions.Clients.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <UsersProjectManagers />
        </Suspense>
      </PermissionGuard>
    ),
  },
  {
    path: ROUTES.USERS_CLIENTS,
    element: (
      <PermissionGuard permissions={[Permissions.Clients.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <UsersClients />
        </Suspense>
      </PermissionGuard>
    ),
  },
  {
    path: ROUTES.USERS_CONTRACTORS,
    element: (
      <PermissionGuard permissions={[Permissions.Clients.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <UsersContractors />
        </Suspense>
      </PermissionGuard>
    ),
  },
  {
    path: ROUTES.USERS_INSPECTORS,
    element: (
      <PermissionGuard permissions={[Permissions.Clients.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <UsersInspectors />
        </Suspense>
      </PermissionGuard>
    ),
  },
];

const propertiesRoutes: RouteObject[] = [
  {
    path: ROUTES.PROPERTIES,
    element: (
      <PermissionGuard permissions={[Permissions.Properties.View]} redirect>
        <Suspense fallback={<PageSuspense />}>
          <Properties />
        </Suspense>
      </PermissionGuard>
    ),
  },
];

const profileRoutes: RouteObject[] = [
  {
    path: `${ROUTES.PROFILE}/:userId`,
    element: (
      <Suspense fallback={<PageSuspense filters={false} />}>
        <UserDetails />
      </Suspense>
    ),
  },
];

const settingsRoutes: RouteObject[] = [
  {
    path: ROUTES.SETTINGS,
    element: (
      <Suspense fallback={<PageSuspense filters={false} />}>
        <Settings />
      </Suspense>
    ),
    children: [
      {
        path: "",
        element: <Navigate to={ROUTES.SETTINGS_PROFILE} />,
      },
      {
        path: ROUTES.SETTINGS_PROFILE,
        element: (
          <Suspense fallback={<PageSuspense filters={false} />}>
            <ProfileSettings />
          </Suspense>
        ),
      },
      {
        path: ROUTES.SETTINGS_PASSWORD,
        element: (
          <Suspense fallback={<PageSuspense filters={false} />}>
            <PasswordSettings />
          </Suspense>
        ),
      },
    ],
  },
];

const legalRoutes: RouteObject[] = [
  {
    path: `/${ROUTES.LEGAL}/*`,
    element: (
      <Suspense fallback={<PageSuspense filters={false} header={false} />}>
        <Legal />
      </Suspense>
    ),
    children: [
      {
        path: ROUTES.COOKIE_NOTICE,
        element: (
          <Suspense fallback={<PageSuspense filters={false} header={false} />}>
            <CookieNotice />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PRIVACY_POLICY,
        element: (
          <Suspense fallback={<PageSuspense filters={false} header={false} />}>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
    ],
  },
];

const tenantLegalRoutes: RouteObject[] = [
  {
    path: `${ROUTES.LEGAL}/*`,
    element: (
      <Suspense fallback={<PageSuspense filters={false} header={false} />}>
        <Legal />
      </Suspense>
    ),
    children: [
      {
        path: ROUTES.COOKIE_NOTICE,
        element: (
          <Suspense fallback={<PageSuspense filters={false} header={false} />}>
            <CookieNotice />
          </Suspense>
        ),
      },
      {
        path: ROUTES.PRIVACY_POLICY,
        element: (
          <Suspense fallback={<PageSuspense filters={false} header={false} />}>
            <PrivacyPolicy />
          </Suspense>
        ),
      },
    ],
  },
];

const routes: RouteObject[] = [
  ...legalRoutes,
  ...authRoutes,
  {
    path: "/:tenant",
    element: <PrivatePage />,
    children: [
      {
        index: true,
        element: <Navigate to={ROUTES.PROJECTS} replace />,
      },
      ...onboardingRoutes,
      ...projectRoutes,
      ...superAdminRoutes,
      ...partnersRoutes,
      ...usersRoutes,
      ...propertiesRoutes,
      ...profileRoutes,
      ...settingsRoutes,
      ...tenantLegalRoutes,
      {
        path: "*",
        element: <Navigate to={ROUTES.PROJECTS} replace />,
      },
    ],
  },
];

export { routes };
